import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { useAuth } from '@/context/AuthContext';
import { useOptionsFlow, useOptionsFlowDates, useOptionsFlowTickers } from '@/hooks/useOptionsFlow';
import Calendar from '@/components/filters/Calendar';
import MultiSelectChips from '@/components/filters/MultiSelectChips';
import OptionsFlowTable from '@/components/options/OptionsFlowTable';
import LoginModal from '@/components/common/LoginModal';
import Disclaimer from '@/components/common/Disclaimer';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { OptionsFlowFilters } from '@/types';

export default function OptionsFlowPage() {
  usePageTitle('期权异动');

  const { user } = useAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<OptionsFlowFilters>({
    date: null,
    tickers: [],
  });

  // Fetch available dates
  const { data: availableDates = [], isLoading: datesLoading } = useOptionsFlowDates();

  // Fetch available tickers for selected date
  const { data: availableTickers = [] } = useOptionsFlowTickers(filters.date);

  // Fetch options flow data
  const effectiveFilters = useMemo(() => ({
    ...filters,
    date: user ? filters.date : null,
  }), [filters, user]);

  const { data: optionsData = [], isLoading: dataLoading, error } = useOptionsFlow(effectiveFilters);

  // Handle date change
  const handleDateChange = useCallback((newDate: string | null) => {
    if (newDate && !user) {
      setLoginModalOpen(true);
      return;
    }
    setFilters((prev) => ({
      ...prev,
      date: newDate,
      tickers: [], // Reset ticker filter when date changes
    }));
  }, [user]);

  // Handle ticker change
  const handleTickersChange = useCallback((newTickers: string[]) => {
    setFilters((prev) => ({
      ...prev,
      tickers: newTickers,
    }));
  }, []);

  const needsAuth = filters.date && !user;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          期权异动
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          追踪期权市场大单异动，发现机构动向
        </Typography>
        <Disclaimer />
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
        }}
      >
        {/* Calendar */}
        <Box sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            选择日期
          </Typography>
          {datesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Calendar
              selected={filters.date}
              onChange={handleDateChange}
              available={availableDates}
            />
          )}
        </Box>

        {/* Ticker Filter */}
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            筛选 Ticker（可选）
          </Typography>
          <MultiSelectChips
            options={availableTickers}
            value={filters.tickers}
            onChange={handleTickersChange}
            placeholder="输入 ticker 搜索..."
            label="Ticker"
          />
          {filters.date && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              共 {availableTickers.length} 个 ticker 有期权异动记录
            </Typography>
          )}
        </Box>

        {/* Stats Summary */}
        {filters.date && user && !dataLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              minWidth: 120,
            }}
          >
            <Typography variant="h4" fontWeight={700} color="primary.main">
              {optionsData.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              条异动记录
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Content */}
      {!filters.date ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            请在上方日历中选择一个日期
          </Typography>
          <Typography variant="body2" color="text.secondary">
            选择日期后即可查看当日期权异动
          </Typography>
        </Box>
      ) : needsAuth ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            请登录以查看期权异动数据
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setLoginModalOpen(true)}
          >
            点击登录
          </Typography>
        </Box>
      ) : (
        <>
          {dataLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              加载失败: {String(error)}
            </Alert>
          )}

          {!dataLoading && !error && (
            <OptionsFlowTable data={optionsData} isLoading={dataLoading} />
          )}
        </>
      )}

      {/* Login Modal */}
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </Box>
  );
}
